/**
 * @description Renders the form's footer in the footer section of the application page.
 */

import React from 'react';
import * as ReactDOM from 'react-dom';
import { PropTypes } from 'prop-types';
import { useFormContext } from 'react-hook-form';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { FooterContext } from '@seccon/core/contexts/footer';
import Spinner from '@seccon/shared/ui/components/Spinner';
import { FooterContainerWithLeftAside } from '@seccon/shared/ui/components/FooterContainer';
import withErrorBoundary from '@seccon/shared/ui/hocs/with-error-boundary';
import { Typography } from '@mui/material';

const styles = {
  actionButton: {
    minWidth: '100px'
  },
  actionButtonContainer: {
    display: 'flex',
    gap: 2
  }
};

/**
 * @prop disabled {boolean} - indicates if the submit button should be disabled
 * @prop isSubmitting {boolean} - indicates if the form is currently submitting
 * @prop onCancel {Function} - callback function for when the cancel button is clicked
 * @prop onSubmit {Function} - callback function for when the submit button is clicked
 */
const FormFooter = ({
  disabled,
  isSubmitting,
  onCancel,
  onSubmit
}) => {
  // Retrieve a reference to the page's footer, so the form footer can be generated in the right place
  const footerEl = React.useContext(FooterContext);

  // Retrive the hidden selected personnel and selected personnel fields
  const { watch } = useFormContext();
  const hiddenSelectedPersonnel = watch('hiddenSelectedPersonnel') ?? {};
  const selectedPersonnel = watch('selectedPersonnel') ?? {};

  // Calculate the hidden selected and selected counts based on the form state
  const hiddenSelectedCount = Object.keys(hiddenSelectedPersonnel).length ?? 0;
  const selectedCount = Object.keys(selectedPersonnel).length ?? 0;

  // Prepare the selected text
  let selectedText = '';

  // If any personnel are selected (hidden or not), add them together and display the total selected
  if (selectedCount > 0 || hiddenSelectedCount > 0) {
    selectedText = `${ selectedCount + hiddenSelectedCount }  Personnel Selected`;
  }

  // If there are any hidden selected personnel, add a suffix to the selected text indicating as much
  if (hiddenSelectedCount > 0) {
    selectedText += ` (${ hiddenSelectedCount } Hidden by Filters)`;
  }

  // If the footer is not defined, do nothing
  if (footerEl === null) {
    return null;
  }

  const content = (
    <FooterContainerWithLeftAside id='contractPersonnelAssignFooter'>
      <Typography fontWeight='bold'>
        {selectedText}
      </Typography>
      <Box sx={styles.actionButtonContainer}>
        <Button onClick={onCancel} variant='outlined' sx={styles.actionButton} id='contractPersonnelAssignClearBtn'>
          Cancel
        </Button>
        <Button onClick={onSubmit} variant='contained' sx={styles.actionButton} disabled={disabled} id='contractPersonnelAssignSubitBtn'>
          {isSubmitting ? <Spinner /> : 'Assign'}
        </Button>
      </Box>
    </FooterContainerWithLeftAside>
  );

  return ReactDOM.createPortal(content, footerEl);
};

FormFooter.propTypes = {
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

FormFooter.defaultProps = {
  disabled: false,
  isSubmitting: false
};

export default withErrorBoundary(FormFooter);
